import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { 
  MapPin, 
  Edit2, 
  Save, 
  X,
  Plus,
  Trash2
} from 'lucide-react';

const ZoneManagementPage = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingZone, setEditingZone] = useState(null);
  const [tempZoneData, setTempZoneData] = useState({});
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pricing/zones');
      if (response.data.success) {
        setZones(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching zones:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Bölgeler yüklenirken hata oluştu"
      });
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (zone) => {
    setEditingZone(zone.id);
    setTempZoneData({
      zone_name: zone.zone_name || '',
      description: zone.description || '',
      locations: zone.locations || ''
    });
  };

  const cancelEdit = () => {
    setEditingZone(null);
    setTempZoneData({});
  };

  const saveZone = async (zoneId) => {
    try {
      setSaving(true);
      await api.put(`/pricing/zones/${zoneId}`, tempZoneData);
      
      toast({
        title: "Başarılı",
        description: "Bölge bilgileri başarıyla güncellendi"
      });
      
      setEditingZone(null);
      setTempZoneData({});
      fetchZones(); // Refresh data
    } catch (error) {
      console.error('Error saving zone:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Bölge kaydedilirken hata oluştu"
      });
    } finally {
      setSaving(false);
    }
  };

  const getZoneDisplayName = (zone) => {
    if (zone.locations && zone.locations.trim()) {
      return `${zone.zone_name} (${zone.locations})`;
    }
    return zone.zone_name;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20 mb-8">
          <div className="flex items-center space-x-4">
            <div className="p-4 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Bölge Yönetimi</h1>
              <p className="text-gray-600 mt-2">
                Her bölgeye ait yerler ve bilgileri düzenleyin
              </p>
            </div>
          </div>
        </div>

        {/* Zones Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {zones.map((zone) => (
            <Card key={zone.id} className="bg-white/60 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-gray-900">
                        {getZoneDisplayName(zone)}
                      </CardTitle>
                      <Badge variant="secondary" className="mt-1">
                        Bölge {zone.zone_number}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEdit(zone)}
                    disabled={editingZone === zone.id}
                    className="rounded-xl"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {editingZone === zone.id ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor={`name-${zone.id}`} className="text-sm font-medium text-gray-700">
                        Bölge Adı
                      </Label>
                      <Input
                        id={`name-${zone.id}`}
                        value={tempZoneData.zone_name}
                        onChange={(e) => setTempZoneData(prev => ({ ...prev, zone_name: e.target.value }))}
                        className="rounded-xl mt-1"
                        placeholder="Bölge adı"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor={`description-${zone.id}`} className="text-sm font-medium text-gray-700">
                        Açıklama
                      </Label>
                      <Input
                        id={`description-${zone.id}`}
                        value={tempZoneData.description}
                        onChange={(e) => setTempZoneData(prev => ({ ...prev, description: e.target.value }))}
                        className="rounded-xl mt-1"
                        placeholder="Açıklama"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor={`locations-${zone.id}`} className="text-sm font-medium text-gray-700">
                        Yerler <span className="text-gray-500">(virgülle ayırın)</span>
                      </Label>
                      <Textarea
                        id={`locations-${zone.id}`}
                        value={tempZoneData.locations}
                        onChange={(e) => setTempZoneData(prev => ({ ...prev, locations: e.target.value }))}
                        className="rounded-xl mt-1"
                        placeholder="Erenköy, Kozyatağı, Yenisahra"
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => saveZone(zone.id)}
                        disabled={saving}
                        className="flex-1 rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                      >
                        {saving ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Kaydet
                      </Button>
                      <Button
                        variant="outline"
                        onClick={cancelEdit}
                        disabled={saving}
                        className="rounded-xl"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Açıklama:</span>
                      <p className="text-gray-900 mt-1">{zone.description || 'Açıklama yok'}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm font-medium text-gray-500">Yerler:</span>
                      {zone.locations ? (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {zone.locations.split(',').map((location, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {location.trim()}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm mt-1">Henüz yer eklenmemiş</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ZoneManagementPage; 